import { useEffect, useRef, useState } from "react";
import { Button } from "../../Button";
import { Modal } from "../../Modal";
import { Body } from "../../Typography/Body";
import { Heading } from "../../Typography/Heading";
import "./CitationMarker.css";

export interface CitationMarkerAction {
  /** `SummaryCitation.id` this menu entry acts on. */
  citationRowId: string;
  /** Display number, assigned by order of appearance in the entry. */
  displayNumber: number;
}

interface CitationMarkerProps {
  /** One entry per source behind this claim, rendered as `[1][2][3]`. */
  actions: readonly CitationMarkerAction[];
  /** Jump the PDF viewer to this citation's page and outline its block. */
  onOpen: (citationRowId: string) => void;
  /** Soft-delete this one link, leaving the claim's other links alone. */
  onRemove: (citationRowId: string) => void;
}

/**
 * The `[1][2]` markers rendered after a cited claim.
 *
 * Each number is a separate link to a separate source block, so removing one
 * leaves the others in place. Clicking opens a two-item menu: *Open* jumps the
 * document viewer to that block, *Remove* retires the link.
 *
 * Removing asks for confirmation first: it is a soft delete recorded against
 * the reviewer (`removedBy`), and the menu sits on a one-character target
 * inside flowing text, so a mis-click is easy and the result is invisible.
 *
 * Design-system note: `DropdownMenu` renders its own icon trigger and cannot
 * take inline text, so the menu here is local rather than composed from it.
 */
export const CitationMarker: React.FC<CitationMarkerProps> = ({
  actions,
  onOpen,
  onRemove,
}) => {
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [confirmingFor, setConfirmingFor] =
    useState<CitationMarkerAction | null>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  // Close on any click outside, matching DropdownMenu's behaviour.
  useEffect(() => {
    if (openFor === null) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpenFor(null);
      }
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [openFor]);

  return (
    <span className="indigo-citation-marker" ref={containerRef}>
      {actions.map((action) => (
        <span
          key={action.citationRowId}
          className="indigo-citation-marker__anchor-wrap"
        >
          <button
            type="button"
            className="indigo-citation-marker__anchor"
            aria-haspopup="menu"
            aria-expanded={openFor === action.citationRowId}
            aria-label={`Source ${action.displayNumber}`}
            onClick={(event) => {
              event.stopPropagation();
              setOpenFor((current) =>
                current === action.citationRowId ? null : action.citationRowId,
              );
            }}
          >
            [{action.displayNumber}]
          </button>
          {openFor === action.citationRowId && (
            <span className="indigo-citation-marker__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="indigo-citation-marker__item"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenFor(null);
                  onOpen(action.citationRowId);
                }}
              >
                Open
              </button>
              <button
                type="button"
                role="menuitem"
                className="indigo-citation-marker__item"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenFor(null);
                  setConfirmingFor(action);
                }}
              >
                Remove
              </button>
            </span>
          )}
        </span>
      ))}

      {/* Portalled to body, but React events still bubble through the tree, so
          the row's click-to-select would fire behind the dialog. */}
      <span
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Modal
          open={confirmingFor !== null}
          onCancel={() => setConfirmingFor(null)}
          footer={null}
          centered
          width={420}
          className="indigo-citation-marker__confirm"
        >
          <Heading level={2}>Remove this source?</Heading>
          <Body size="md" className="indigo-citation-marker__confirm-body">
            {confirmingFor
              ? `Source [${confirmingFor.displayNumber}] stops showing on this entry. Its other sources stay.`
              : ""}
          </Body>
          <div className="indigo-citation-marker__confirm-actions">
            <Button variant="ghost" onClick={() => setConfirmingFor(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (confirmingFor) onRemove(confirmingFor.citationRowId);
                setConfirmingFor(null);
              }}
            >
              Remove
            </Button>
          </div>
        </Modal>
      </span>
    </span>
  );
};
